// Shared binding-resolver helpers used by ecommerce blocks (and any
// future block that wants the transaction-embed-style ArgMapping UX
// without paying the cost of EmbedPreview).
//
// Convention: ArgMapping is used in TWO modes throughout ecommerce
// blocks. The shape is the same; only the call site differs.
//
//   1. Read-mode (block inputs):
//        target  = API param name (e.g. "customerId", "q")
//        source  = page_input | static — the VALUE read at runtime
//      Pass to `resolveArgsAsRecord` to build the API call payload.
//
//   2. Write-mode (block outputs):
//        target  = a key on the result object the block emits
//                  (e.g. "value" for the whole record, "id", "orderId")
//        source  = page_input — `source.name` is the DESTINATION
//                  page variable that should receive `values[target]`.
//      Pass to `writeOutputs` to bulk-write all configured outputs.
//
// `outputVarName` extracts the destination var name for the common
// single-binding case (cartBinding, gateBinding, etc.) where you want
// to both read from and write to the same variable.

import type { ArgMapping, FilterValueRef, PageInput } from '@/pages/designers/page/types'

export interface ResolveCtx {
  vars: Record<string, unknown>
  inputs: PageInput[]
}

/** Read the resolved value of a FilterValueRef. Mirrors the existing
 *  page_input + static handling in useEmbedRecords.resolveRef. */
export function resolveRef(ref: FilterValueRef | undefined, ctx: ResolveCtx): unknown {
  if (!ref) return undefined
  if (ref.source === 'static') return ref.value ?? ''
  if (ref.source === 'page_input' && ref.name) return ctx.vars[ref.name]
  return undefined
}

/** Build a `{target: resolvedValue}` map for an ArgMapping[]. Used to
 *  feed API call params / body. Skips rows with empty target or with
 *  an unresolved source (undefined / empty string). */
export function resolveArgsAsRecord(
  args: ArgMapping[] | undefined,
  ctx: ResolveCtx,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const a of args || []) {
    if (!a.target) continue
    const v = resolveRef(a.source, ctx)
    if (v === undefined || v === '') continue
    out[a.target] = v
  }
  return out
}

/** Returns the page-input name a binding writes into (output mode), or
 *  reads from (single-binding shorthand for cartBinding etc.). Returns
 *  null when the binding isn't a page_input source. */
export function outputVarName(m: ArgMapping | undefined): string | null {
  if (!m) return null
  if (m.source.source !== 'page_input') return null
  return m.source.name?.trim() || null
}

/** Bulk-resolve `outputBindings` for a write. For each row whose source
 *  is a page_input with a configured name, write `values[target]` into
 *  that page var. `target === 'value'` is the convention for "the whole
 *  emitted record" — when no key matches `target`, falls back to
 *  `values.value`. */
export function writeOutputs(
  outputs: ArgMapping[] | undefined,
  values: Record<string, unknown> & { value?: unknown },
  setVar: (name: string, value: unknown) => void,
): void {
  for (const o of outputs || []) {
    const dest = outputVarName(o)
    if (!dest) continue
    const v = o.target in values ? values[o.target]
      : o.target === 'value' ? values.value
      : undefined
    if (v === undefined) continue
    setVar(dest, v)
  }
}

/** Reads the value of a single-binding (e.g. cartBinding). When the
 *  binding is a page_input it returns the current variable value; when
 *  it's a static it returns the static value (rare for read sites). */
export function readBinding(binding: ArgMapping | undefined, ctx: ResolveCtx): unknown {
  return resolveRef(binding?.source, ctx)
}
